// src/pages/billing/InvoiceDetailsPage.jsx - Fixed calculations
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getInvoiceById } from '../../services/billingService';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { printInvoice, downloadInvoiceAsPDF } from '../../utils/printUtils';

const InvoiceDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { vendorProfile } = useAuth();
  const { showAlert } = useNotification();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInvoice();
  }, [id]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const invoiceData = await getInvoiceById(id);
      if (!invoiceData) {
        showAlert('Invoice not found', 'error');
        navigate('/billing');
        return;
      }
      setInvoice(invoiceData);
    } catch (error) {
      console.error('Error loading invoice:', error);
      showAlert('Failed to load invoice details', 'error');
      navigate('/billing');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePrint = () => {
    const success = printInvoice(invoice, vendorProfile);
    if (!success) {
      showAlert('Please allow popups to print invoices', 'error');
    }
  };

  const handleDownload = () => {
    const success = downloadInvoiceAsPDF(invoice, vendorProfile);
    if (!success) {
      showAlert('Please allow popups to download invoices', 'error');
    }
  };

  const handleSendWhatsApp = () => {
    try {
      const companyName = vendorProfile?.shopName || 'Your Company';
      const invoiceUrl = `${window.location.origin}/billing/invoice/${invoice.id}`;
      const itemsCount = invoice.items?.length || 0;
      const totalQuantity = invoice.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
      
      const message = encodeURIComponent(
        `Dear ${invoice.customerName || 'Customer'},

Thank you for your business!

*Invoice Details:*
📋 Invoice Number: ${invoice.invoiceNumber}
🏪 From: ${companyName}
💰 Amount: ${formatCurrency(invoice.totalAmount)}
📅 Date: ${formatDate(invoice.createdAt)}
📦 Items: ${itemsCount} (Total Qty: ${totalQuantity})
✅ Status: Paid
${invoice.invoiceType === 'with_gst' ? '🧾 GST Invoice' : '📄 Non-GST Invoice'}

View Invoice: ${invoiceUrl}

Please contact us for any queries.
Best regards,
${companyName}`
      );

      const whatsappUrl = `https://wa.me/?text=${message}`;
      window.open(whatsappUrl, '_blank');
    } catch (error) {
      console.error('Error generating WhatsApp link:', error);
      showAlert('Failed to generate WhatsApp link', 'error');
    }
  };

  // Helper function to calculate CGST and SGST - FIXED
  const calculateCGSTSGST = (taxableAmount, gstRate) => {
    const totalGSTAmount = (taxableAmount * gstRate) / 100;
    const cgstAmount = totalGSTAmount / 2;
    const sgstAmount = totalGSTAmount / 2;
    return { cgstAmount, sgstAmount, totalGSTAmount };
  };

  // Get GST breakdown by rate for display - FIXED
  const getGstBreakdown = () => {
    if (!invoice?.items || invoice.invoiceType !== 'with_gst') return {};
    
    const gstBreakdown = {};
    const discountPercentage = parseFloat(invoice.discount) || 0;
    
    invoice.items.forEach(item => {
      const rate = item.gstRate || 0;
      const itemDiscountAmount = (item.amount || 0) * discountPercentage / 100;
      const itemTaxableAmount = (item.amount || 0) - itemDiscountAmount;
      const { cgstAmount, sgstAmount, totalGSTAmount } = calculateCGSTSGST(itemTaxableAmount, rate);

      if (rate > 0) {
        if (!gstBreakdown[rate]) {
          gstBreakdown[rate] = { taxableAmount: 0, cgstAmount: 0, sgstAmount: 0, totalGSTAmount: 0 };
        }
        gstBreakdown[rate].taxableAmount += itemTaxableAmount;
        gstBreakdown[rate].cgstAmount += cgstAmount;
        gstBreakdown[rate].sgstAmount += sgstAmount;
        gstBreakdown[rate].totalGSTAmount += totalGSTAmount;
      }
    });
    return gstBreakdown;
  };

  // Calculate item total with GST - NEW FUNCTION
  const calculateItemTotalWithGST = (item, discountPercentage = 0) => {
    const itemDiscountAmount = (item.amount * discountPercentage) / 100;
    const itemTaxableAmount = item.amount - itemDiscountAmount;
    if (invoice.invoiceType === 'with_gst') {
      const { totalGSTAmount } = calculateCGSTSGST(itemTaxableAmount, item.gstRate || 0);
      return itemTaxableAmount + totalGSTAmount;
    }
    return itemTaxableAmount;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!invoice) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900">Invoice not found</h3>
            <p className="mt-2 text-gray-500">The invoice you're looking for doesn't exist.</p>
            <Link
              to="/billing"
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
            >
              Back to Billing
            </Link>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const discountAmount = (invoice.subtotal * (parseFloat(invoice.discount) || 0)) / 100;
  const gstBreakdown = getGstBreakdown();
  
  // Calculate actual total GST from breakdown
  const actualTotalGST = Object.values(gstBreakdown).reduce((sum, data) => sum + data.totalGSTAmount, 0);
  
  // Calculate final total: subtotal - discount + GST
  const finalTotal = (invoice.subtotal || 0) - discountAmount + (invoice.invoiceType === 'with_gst' ? actualTotalGST : 0);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="py-4 flex justify-between items-center border-b border-gray-200">
          <div className="flex items-center">
            <Link
              to="/billing"
              className="mr-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Invoice {invoice.invoiceNumber}</h1>
              <p className="mt-1 text-sm text-gray-600">
                Created and paid on {formatDate(invoice.createdAt)}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Paid
            </span>

            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              invoice.invoiceType === 'with_gst' 
                ? 'bg-blue-100 text-blue-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {invoice.invoiceType === 'with_gst' ? 'GST Invoice' : 'Non-GST Invoice'}
            </span>

            <div className="flex space-x-2">
              <button
                onClick={handleSendWhatsApp}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Send
              </button>

              <button
                onClick={handleDownload}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download
              </button>

              <button
                onClick={handlePrint}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print
              </button>
            </div>
          </div>
        </div>

        {/* Invoice Content */}
        <div className="mt-8 bg-white shadow-lg rounded-lg">
          <div className="px-8 py-8">
            {/* Invoice Header */}
            <div className="flex justify-between items-start mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">INVOICE</h2>
                <div className="text-sm text-gray-600">
                  <p><strong>Invoice #:</strong> {invoice.invoiceNumber}</p>
                  <p><strong>Date:</strong> {formatDate(invoice.createdAt)}</p>
                  <p><strong>Payment Date:</strong> {formatDate(invoice.paidAt || invoice.createdAt)}</p>
                  <p><strong>Status:</strong> 
                    <span className="ml-1 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      PAID
                    </span>
                  </p>
                </div>
              </div>

              {/* Vendor Details */}
              {invoice.invoiceType === 'with_gst' && (
                <div className="text-right">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {vendorProfile?.shopName || 'ZoneXVendors'}
                  </h3>
                  <div className="text-sm text-gray-600">
                    <p>{vendorProfile?.address || 'Shop Address'}</p>
                    <p>{vendorProfile?.city || 'City'}, {vendorProfile?.state || 'State'} {vendorProfile?.pincode || 'PIN'}</p>
                    <p>Phone: {vendorProfile?.phone || 'N/A'}</p>
                    <p>Email: {vendorProfile?.email || 'N/A'}</p>
                    {vendorProfile?.gstNumber && (
                      <p>GST: {vendorProfile.gstNumber}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Customer Details */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Bill To:</h4>
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="font-medium text-gray-900">{invoice.customerName}</p>
                {invoice.customerEmail && <p className="text-sm text-gray-600">{invoice.customerEmail}</p>}
                {invoice.customerPhone && <p className="text-sm text-gray-600">{invoice.customerPhone}</p>}
                {invoice.invoiceType === 'with_gst' && invoice.customerGST && (
                  <p className="text-sm text-gray-600">GST: {invoice.customerGST}</p>
                )}
                {invoice.customerAddress && (
                  <p className="text-sm text-gray-600 mt-2">{invoice.customerAddress}</p>
                )}
              </div>
            </div>

            {/* Invoice Items */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Invoice Items</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                        Description
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                        HSN
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                        Qty
                      </th>
                      <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                        Rate (₹)
                      </th>
                      {invoice.invoiceType === 'with_gst' && (
                        <>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                            GST (%)
                          </th>
                          <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                            CGST (₹)
                          </th>
                          <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                            SGST (₹)
                          </th>
                        </>
                      )}
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                        Amount (₹)
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total (₹)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoice.items?.map((item, index) => {
                      const discountPercentage = parseFloat(invoice.discount) || 0;
                      const itemDiscountAmount = (item.amount * discountPercentage) / 100;
                      const itemTaxableAmount = item.amount - itemDiscountAmount;
                      const { cgstAmount, sgstAmount } = invoice.invoiceType === 'with_gst' ?
                        calculateCGSTSGST(itemTaxableAmount, item.gstRate || 0) :
                        { cgstAmount: 0, sgstAmount: 0 };
                      const totalWithGST = calculateItemTotalWithGST(item, discountPercentage);

                      return (
                        <tr key={index}>
                          <td className="px-3 py-4 border-r">
                            <div className="text-sm font-medium text-gray-900">{item.name}</div>
                            {item.productId && (
                              <div className="text-xs text-gray-500">Product ID: {item.productId}</div>
                            )}
                          </td>
                          <td className="px-2 py-4 text-center border-r">
                            <span className="text-xs text-gray-900">{item.hsnCode || 'N/A'}</span>
                          </td>
                          <td className="px-2 py-4 text-center border-r">
                            <span className="text-sm text-gray-900">{item.quantity}</span>
                          </td>
                          <td className="px-2 py-4 text-right border-r">
                            <span className="text-sm text-gray-900">{formatCurrency(item.rate)}</span>
                          </td>
                          {invoice.invoiceType === 'with_gst' && (
                            <>
                              <td className="px-2 py-4 text-center border-r">
                                <span className="text-sm text-gray-900">{item.gstRate || 0}%</span>
                              </td>
                              <td className="px-2 py-4 text-right border-r">
                                <span className="text-xs text-gray-900">{formatCurrency(cgstAmount)}</span>
                              </td>
                              <td className="px-2 py-4 text-right border-r">
                                <span className="text-xs text-gray-900">{formatCurrency(sgstAmount)}</span>
                              </td>
                            </>
                          )}
                          <td className="px-3 py-4 text-right border-r">
                            <span className="text-sm font-medium text-gray-900">{formatCurrency(item.amount)}</span>
                          </td>
                          <td className="px-3 py-4 text-right">
                            <span className="text-sm font-bold text-gray-900">{formatCurrency(totalWithGST)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Invoice Totals */}
            <div className="flex justify-end mb-8">
              <div className="w-96">
                <div className="bg-gray-50 p-6 rounded-md border">
                  <div className="space-y-2">
                    <div className="flex justify-between py-1">
                      <span className="text-sm text-gray-600">Subtotal:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(invoice.subtotal || 0)}
                      </span>
                    </div>

                    {(invoice.discount && parseFloat(invoice.discount) > 0) && (
                      <div className="flex justify-between py-1">
                        <span className="text-sm text-gray-600">Discount ({invoice.discount}%):</span>
                        <span className="text-sm font-medium text-gray-900">
                          -{formatCurrency(discountAmount)}
                        </span>
                      </div>
                    )}

                    {/* GST Breakdown */}
                    {invoice.invoiceType === 'with_gst' && Object.keys(gstBreakdown).length > 0 && (
                      <div className="border-t border-gray-200 pt-2">
                        <div className="text-xs font-medium text-gray-700 mb-1">GST Breakdown:</div>
                        {Object.entries(gstBreakdown).map(([rate, data]) => (
                          <div key={rate} className="space-y-1 mb-2">
                            <div className="flex justify-between py-1">
                              <span className="text-xs text-gray-600">CGST @ {rate / 2}%:</span>
                              <span className="text-xs font-medium text-gray-900">{formatCurrency(data.cgstAmount)}</span>
                            </div>
                            <div className="flex justify-between py-1">
                              <span className="text-xs text-gray-600">SGST @ {rate / 2}%:</span>
                              <span className="text-xs font-medium text-gray-900">{formatCurrency(data.sgstAmount)}</span>
                            </div>
                          </div>
                        ))}
                        <div className="flex justify-between py-1 border-t border-gray-200 mt-1 pt-1">
                          <span className="text-sm text-gray-600">Total GST:</span>
                          <span className="text-sm font-medium text-gray-900">{formatCurrency(invoice.totalTaxAmount || 0)}</span>
                        </div>
                      </div>
                    )}

                    <div className="border-t border-gray-200 mt-4 pt-4">
                      <div className="flex justify-between">
                        <span className="text-lg font-semibold text-gray-900">Total Paid:</span>
                        <span className="text-lg font-bold text-green-600">
                          {formatCurrency(finalTotal)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes and Terms */}
            {(invoice.notes || invoice.terms) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                {invoice.notes && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Notes:</h4>
                    <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-md">
                      {invoice.notes}
                    </div>
                  </div>
                )}

                {invoice.terms && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Terms & Conditions:</h4>
                    <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-md">
                      {invoice.terms}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="border-t border-gray-200 pt-6 mt-8">
              <div className="text-center text-sm text-gray-500">
                <p>Thank you for your business!</p>
                <p className="mt-2">This is a computer generated invoice.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default InvoiceDetailsPage;